import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  Flag,
  ThumbsUp,
  ThumbsDown,
  Clock,
  AlertCircle,
  BookOpen,
  Save,
  Copy,
  Check,
  Star,
  User,
  Headphones,
} from "lucide-react";
import { conversationsApi, alphaConversationsApi } from "../api/client";
import { fmtDate, fmtMs, langLabel, themeLabel } from "../utils/format";
import { useAuth } from "../auth/AuthContext";
import {
  getDisplayReferences,
  getSafeHttpUrl,
  normalizeConversationMessage,
  rewriteCitations,
  tokenizeHttpUrls,
} from "../utils/conversationMessage";

const FLAG_STATUSES = [
  { v: "open", label: "Open" },
  { v: "reviewed", label: "Reviewed" },
  { v: "actioned", label: "Actioned" },
  { v: "dismissed", label: "Dismissed" },
];

const Skeleton = ({
  width = "100%",
  height = 20,
  borderRadius = 4,
  style = {},
}) => (
  <div
    className="skeleton-box"
    style={{
      display: "inline-block",
      width,
      height,
      borderRadius,
      ...style,
    }}
  />
);

const LinkedText = ({ children }) => (
  <>
    {tokenizeHttpUrls(children).map((token, index) =>
      token.type === "link" ? (
        <a
          key={`${token.href}-${index}`}
          href={token.href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "var(--saffron-deep)",
            fontWeight: 600,
            textDecoration: "underline",
            textUnderlineOffset: 2,
            overflowWrap: "anywhere",
          }}
        >
          {token.value}
        </a>
      ) : (
        <span key={`text-${index}`}>{token.value}</span>
      ),
    )}
  </>
);

const ConversationAnswer = ({ message, isUser }) => {
  const [expanded, setExpanded] = useState(false);
  const detailsRef = useRef(null);
  const answer = normalizeConversationMessage(message);
  const references = getDisplayReferences(answer.sourceReferences);
  const shortAnswer = rewriteCitations(answer.shortAnswer, answer.sourceReferences, references);
  const longAnswer = rewriteCitations(answer.longAnswer, answer.sourceReferences, references);
  const canExpand = !isUser && (!!longAnswer || references.length > 0);

  useLayoutEffect(() => {
    if (!expanded || !detailsRef.current) return;
    const scroller = document.scrollingElement;
    if (!scroller) return;
    scroller.scrollTop = Math.max(
      0,
      scroller.scrollTop + detailsRef.current.getBoundingClientRect().top - 12,
    );
  }, [expanded]);

  return (
    <>
      <div style={{ whiteSpace: "pre-wrap" }}>
        <LinkedText>{shortAnswer}</LinkedText>
      </div>

      {expanded && canExpand && (
        <div ref={detailsRef} style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--ink-100)" }}>
          {longAnswer && (
            <div style={{ whiteSpace: "pre-wrap" }}>
              <LinkedText>{longAnswer}</LinkedText>
            </div>
          )}
          {references.length > 0 && (
            <section aria-label="Sources & References" style={{ marginTop: longAnswer ? 20 : 0, whiteSpace: "normal" }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: "var(--ink-800)" }}>
                Sources &amp; References
              </div>
              <div style={{ display: "grid", gap: 8, minWidth: 0 }}>
                {references.map((source, index) => {
                  const sourceUrl = getSafeHttpUrl(source.source);
                  return (
                    <div
                      key={`${source.index}-${index}`}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        padding: 12,
                        border: "1px solid var(--ink-200)",
                        borderRadius: 12,
                        background: "var(--paper-2)",
                        minWidth: 0,
                      }}
                    >
                      <span style={{ color: "var(--saffron-deep)", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                        [{source.displayIndex}]
                      </span>
                      <div style={{ minWidth: 0, overflowWrap: "anywhere", fontSize: 12, lineHeight: 1.5 }}>
                        <div style={{ fontWeight: 700, color: "var(--ink-800)" }}>{source.title}</div>
                        {source.excerpt && (
                          <div style={{ marginTop: 4, color: "var(--ink-600)", whiteSpace: "pre-wrap" }}>
                            {source.excerpt}
                          </div>
                        )}
                        {sourceUrl && (
                          <a
                            href={sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: "inline-block",
                              marginTop: 7,
                              color: "var(--saffron-deep)",
                              fontWeight: 700,
                              textDecoration: "underline",
                              textUnderlineOffset: 2,
                            }}
                          >
                            Open source ↗
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      )}

      {canExpand && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded(value => !value)}
          style={{
            display: "block",
            marginTop: 12,
            padding: "3px 0",
            border: "none",
            background: "transparent",
            color: "var(--saffron-deep)",
            font: "inherit",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          {expanded ? "Show less ↑" : "View detailed answer ↓"}
        </button>
      )}
    </>
  );
};

export default function ConversationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAlpha = searchParams.get("type") === "alpha";
  const convApi = isAlpha ? alphaConversationsApi : conversationsApi;
  const { hasPermission } = useAuth();
  const canFlag = hasPermission("conversations:flag");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summarising, setSummarising] = useState(false);
  const [error, setError] = useState(null);
  const [flagDrafts, setFlagDrafts] = useState({}); // { messageId: { status, notes } }
  const [isCopied, setIsCopied] = useState(false);
  const [expandedFlagDetails, setExpandedFlagDetails] = useState({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    convApi
      .detail(id)
      .then((d) => !cancelled && setData(d))
      .catch(
        (e) => !cancelled && setError(e?.response?.data?.error || e.message),
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleSummarise = async () => {
    setSummarising(true);
    try {
      const r = await conversationsApi.summarise(id);
      setData((d) => ({
        ...d,
        conversation: {
          ...d.conversation,
          summary: r.summary,
          summary_generated_at: r.generated_at,
        },
      }));
    } catch (e) {
      alert(e?.response?.data?.error || "Could not summarise.");
    } finally {
      setSummarising(false);
    }
  };

  const toggleFlagDetails = (messageId) => {
    setExpandedFlagDetails((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  const formatFeedbackReferences = (value) => {
    if (!value) return null;
    const trimmed = String(value).trim();
    if (!trimmed) return null;

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean);
      }
      if (typeof parsed === "string") {
        return [parsed];
      }
      if (parsed && typeof parsed === "object") {
        return [JSON.stringify(parsed, null, 2)];
      }
    } catch {
      // fall back to plain text below
    }

    return trimmed;
  };

  const submitFlagReview = async (messageId) => {
    const draft = flagDrafts[messageId];
    if (!draft?.status) return;
    try {
      await convApi.reviewFlag(id, messageId, {
        status: draft.status,
        notes: draft.notes || "",
      });
      setData((d) => ({
        ...d,
        messages: d.messages.map((m) =>
          m._id === messageId
            ? {
                ...m,
                flag_status: draft.status,
                flag_review_notes: draft.notes || "",
              }
            : m,
        ),
      }));
      setFlagDrafts((s) => ({ ...s, [messageId]: undefined }));
    } catch (e) {
      alert(e?.response?.data?.error || "Could not save review.");
    }
  };

  if (loading)
    return (
      <div style={{ width: "100%", paddingBottom: 64 }}>
        <style>{`
          @keyframes skeleton-loading {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          .skeleton-box {
            background: linear-gradient(90deg, var(--ink-100, #f3f4f6) 25%, var(--ink-200, #e5e7eb) 50%, var(--ink-100, #f3f4f6) 75%);
            background-size: 200% 100%;
            animation: skeleton-loading 1.5s infinite linear;
          }
        `}</style>

        {/* Back link skeleton */}
        <Skeleton
          width={60}
          height={16}
          style={{ marginBottom: 28, display: "block" }}
        />

        {/* Header skeleton */}
        <div style={{ marginBottom: 32 }}>
          <Skeleton
            width={180}
            height={16}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Skeleton
            width="60%"
            height={40}
            style={{ marginBottom: 12, display: "block" }}
          />
          <div style={{ display: "flex", gap: 10 }}>
            <Skeleton width={90} height={24} borderRadius={12} />
            <Skeleton width={110} height={24} borderRadius={12} />
            <Skeleton width={130} height={24} borderRadius={12} />
          </div>
        </div>

        {/* Transcript Panel skeleton */}
        <div
          className="panel"
          style={{
            background: "#ffffff",
            borderRadius: 16,
            border: "1px solid var(--ink-100)",
            boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "24px 32px",
              background: "rgba(251, 246, 238, 0.3)",
              borderBottom: "1px solid var(--ink-100)",
            }}
          >
            <Skeleton width={200} height={28} />
          </div>
          <div style={{ padding: "32px 32px 8px 32px" }}>
            {[...Array(4)].map((_, index) => {
              const isUser = index % 2 === 0;
              return (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    flexDirection: isUser ? "row-reverse" : "row",
                    marginBottom: 32,
                    gap: 16,
                  }}
                >
                  <Skeleton
                    width={40}
                    height={40}
                    borderRadius="50%"
                    style={{ flexShrink: 0 }}
                  />
                  <div style={{ width: "75%" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: isUser ? "flex-end" : "flex-start",
                        marginBottom: 6,
                      }}
                    >
                      <Skeleton width={120} height={14} />
                    </div>
                    <Skeleton
                      width="100%"
                      height={isUser ? 60 : 100}
                      borderRadius={16}
                      style={{
                        borderTopRightRadius: isUser ? 4 : 16,
                        borderTopLeftRadius: !isUser ? 4 : 16,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );

  if (error)
    return (
      <div
        style={{
          padding: "16px 20px",
          borderRadius: 12,
          border: "1px solid var(--red)",
          background: "#fff1f2",
          color: "var(--red)",
          margin: "24px 0",
          display: "flex",
          alignItems: "center",
          gap: 12,
          fontWeight: 500,
        }}
      >
        <AlertCircle size={20} />
        {error}
      </div>
    );
  if (!data) return null;

  const { conversation: conv, messages } = data;

  return (
    <div style={{ width: "100%", paddingBottom: 64 }}>
      {/* Back link */}
      <button
        onClick={() => navigate(-1)}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: "var(--ink-500)",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12,
          fontWeight: 600,
          marginBottom: 28,
          padding: 0,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          transition: "color 0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ink-900)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ink-500)")}
      >
        <ArrowLeft size={14} /> Back
      </button>

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 32,
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <div>
          <div
            className="t-eyebrow"
            style={{
              color: "var(--ink-500)",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
            }}
          >
            <span>{isAlpha ? "Alpha" : "Beta"} Conversation ·</span>
            <code
              style={{
                fontFamily: "var(--mono)",
                fontSize: 11,
                background: "var(--ink-100)",
                padding: "2px 6px",
                borderRadius: 4,
                color: "var(--ink-600)",
                letterSpacing: "0",
                wordBreak: "break-all",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {conv.session_id}
              <button
                onClick={() => {
                  navigator.clipboard.writeText(conv.session_id);
                  setIsCopied(true);
                  setTimeout(() => setIsCopied(false), 2000);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: isCopied ? "var(--green)" : "var(--ink-400)",
                  display: "flex",
                  alignItems: "center",
                }}
                title="Copy Session ID"
              >
                {isCopied ? <Check size={12} /> : <Copy size={12} />}
              </button>
            </code>
          </div>
          <h1
            className="t-display"
            style={{
              fontSize: 36,
              margin: "0 0 12px 0",
              color: "var(--ink-900)",
            }}
          >
            Dialogue from{" "}
            <em style={{ color: "var(--saffron-deep)", fontStyle: "italic" }}>
              {fmtDate(conv.started_at, {
                hour: undefined,
                minute: undefined,
                hour12: undefined,
              })}
            </em>
          </h1>
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
              fontSize: 13,
              color: "var(--ink-600)",
              fontWeight: 500,
            }}
          >
            <span
              className="badge badge-outline"
              style={{ padding: "4px 10px" }}
            >
              {langLabel(conv.language)}
            </span>
            <span
              className="badge badge-neutral"
              style={{ padding: "4px 10px" }}
            >
              {conv.message_count} messages
            </span>
            {conv.unanswered_count > 0 && (
              <span className="badge badge-red" style={{ padding: "4px 10px" }}>
                {conv.unanswered_count} unanswered
              </span>
            )}
            {conv.flagged_count > 0 && (
              <span
                className="badge badge-saffron"
                style={{ padding: "4px 10px" }}
              >
                {conv.flagged_count} flagged
              </span>
            )}
            <span style={{ color: "var(--ink-400)", margin: "0 4px" }}>·</span>
            {/* <span>avg latency {fmtMs(conv.avg_latency_ms)}</span> */}
          </div>
        </div>
      </div>

      {/* Summary */}
      {conv.summary && (
        <div
          className="panel"
          style={{
            padding: "28px 32px",
            marginBottom: 24,
            background:
              "linear-gradient(135deg, var(--saffron-tint) 0%, #ffffff 100%)",
            borderColor: "var(--saffron-light)",
            borderRadius: 16,
            boxShadow: "0 4px 20px -4px rgba(194, 108, 27, 0.06)",
          }}
        >
          <div
            className="t-eyebrow"
            style={{
              marginBottom: 12,
              color: "var(--saffron-deep)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Sparkles size={14} /> AI summary
          </div>
          <div
            style={{
              fontFamily: "var(--serif)",
              fontSize: 18,
              fontStyle: "italic",
              color: "var(--ink-900)",
              lineHeight: 1.6,
            }}
          >
            {conv.summary}
          </div>
          {conv.summary_generated_at && (
            <div
              style={{
                fontSize: 12,
                color: "var(--ink-500)",
                marginTop: 12,
                fontWeight: 500,
              }}
            >
              Generated {fmtDate(conv.summary_generated_at)}
            </div>
          )}
        </div>
      )}

      {/* Topics & themes */}
      {conv.topics?.length || conv.themes?.length ? (
        <div
          className="panel"
          style={{
            padding: "20px 28px",
            marginBottom: 32,
            borderRadius: 16,
            background: "#ffffff",
            border: "1px solid var(--ink-100)",
            boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
            {conv.themes?.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span className="t-eyebrow" style={{ color: "var(--ink-500)" }}>
                  Themes:
                </span>
                <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {conv.themes.map((t) => (
                    <span
                      key={t}
                      className="badge badge-neutral"
                      style={{ padding: "4px 10px", fontSize: 12 }}
                    >
                      {themeLabel(t)}
                    </span>
                  ))}
                </span>
              </div>
            )}
            {conv.topics?.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span className="t-eyebrow" style={{ color: "var(--ink-500)" }}>
                  Topics:
                </span>
                <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {conv.topics.map((t) => (
                    <span
                      key={t}
                      className="badge badge-saffron"
                      style={{ padding: "4px 10px", fontSize: 12 }}
                    >
                      {t}
                    </span>
                  ))}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Transcript */}
      <div
        className="panel"
        style={{
          background: "#ffffff",
          borderRadius: 16,
          border: "1px solid var(--ink-100)",
          boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
          overflow: "hidden",
        }}
      >
        {/* panel-header removed: Full transcript header intentionally hidden */}
        <div style={{ padding: "32px 32px 8px 32px" }}>
          {messages.map((m, index) => {
            const isUser = m.role === "user";
            const isDevanagari =
              m.language?.startsWith("hi") || m.language?.startsWith("mr");
            const isBlocked =
              m.guardrail_status === "blocked" ||
              m.guardrail_target === "question_blocked" ||
              m.guardrail_target === "answer_blocked";
            const hasFeedback = m.feedback === "up" || m.feedback === "down";
            const hasRating = m.rating != null && m.rating > 0;
            const feedbackReferences = formatFeedbackReferences(
              m.feedback_references,
            );
            const draft = flagDrafts[m._id] || {
              status: m.flag_status || "open",
              notes: m.flag_review_notes || "",
            };

            return (
              <div
                key={m._id}
                style={{
                  display: "flex",
                  flexDirection: isUser ? "row-reverse" : "row",
                  marginBottom: 32,
                  gap: 16,
                }}
              >
                {/* Avatar */}
                <div
                  style={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    borderRadius: "50%",
                    border: isUser ? "1px solid var(--ink-200)" : "none",
                    color: isUser ? "var(--ink-700)" : "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "var(--devanagari)",
                    fontWeight: 600,
                    fontSize: 16,
                    overflow: "hidden",
                    background: isUser ? "var(--paper-2)" : "transparent",
                  }}
                >
                  {isUser ? (
                    <User size={18} />
                  ) : (
                    <img
                      src="/sm-icon.svg"
                      alt="Assistant"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>

                {/* Bubble Container */}
                <div style={{ maxWidth: "75%", minWidth: 0 }}>
                  {/* Meta header */}
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--ink-500)",
                      fontWeight: 500,
                      textAlign: isUser ? "right" : "left",
                      marginBottom: 6,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      justifyContent: isUser ? "flex-end" : "flex-start",
                    }}
                  >
                    <span
                      style={{
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        fontWeight: 700,
                        color: "var(--ink-700)",
                      }}
                    >
                      {isUser ? "Seeker" : "Samvad Mitra"}
                    </span>
                    <span style={{ color: "var(--ink-300)" }}>|</span>
                    <span>{fmtDate(m.createdAt)}</span>
                    {/* {!isUser && m.latency_ms && (
                      <>
                        <span style={{ color: "var(--ink-300)" }}>|</span>
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Clock size={12} />
                          {fmtMs(m.latency_ms)}
                        </span>
                      </>
                    )} */}
                  </div>

                  {/* Message Bubble */}
                  <div
                    style={{
                      background: isBlocked
                        ? "#fff1f2"
                        : isUser
                          ? "var(--paper-2)"
                          : "#ffffff",
                      border: `1px solid ${isBlocked ? "var(--red)" : isUser ? "var(--ink-200)" : "var(--ink-200)"}`,
                      borderRadius: 16,
                      borderTopRightRadius: isUser ? 4 : 16,
                      borderTopLeftRadius: !isUser ? 4 : 16,
                      padding: "16px 20px",
                      boxShadow: isBlocked
                        ? "0 4px 16px -6px rgba(220, 38, 38, 0.22)"
                        : "0 2px 8px -2px rgba(0,0,0,0.02)",
                      fontFamily: isDevanagari
                        ? "var(--devanagari)"
                        : "var(--sans)",
                      fontSize: isDevanagari ? 16 : 15,
                      lineHeight: 1.6,
                      color: isBlocked ? "var(--red)" : "var(--ink-900)",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    <ConversationAnswer message={m} isUser={isUser} />
                  </div>

                  {isBlocked && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: "10px 12px",
                        borderRadius: 12,
                        background: "#fff1f2",
                        border: "1px solid rgba(220, 38, 38, 0.18)",
                        color: "var(--red)",
                        fontSize: 13,
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <AlertCircle size={14} />
                      Blocked by guardrail
                      {m.guardrail_reason ? `: ${m.guardrail_reason}` : ""}
                    </div>
                  )}

                  {/* Badges / Feedback */}
                  {!isUser && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        marginTop: 10,
                        alignItems: "center",
                        fontSize: 12,
                      }}
                    >
                      {m.is_unanswered && (
                        <span
                          className="badge badge-red"
                          style={{ padding: "3px 8px" }}
                        >
                          <AlertCircle
                            size={10}
                            style={{ verticalAlign: "middle", marginRight: 4 }}
                          />
                          Unanswered
                        </span>
                      )}
                      {m.warning === "script_mismatch" && (
                        <span
                          className="badge badge-saffron"
                          style={{ padding: "3px 8px" }}
                        >
                          Script mismatch
                        </span>
                      )}
                      {hasFeedback && (
                        <span
                          style={{
                            color: m.feedback === "up" ? "var(--green)" : "var(--red)",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            fontWeight: 500,
                          }}
                        >
                          {m.feedback === "up" ? <ThumbsUp size={14} /> : <ThumbsDown size={14} />}
                          {m.feedback === "up" ? "Helpful" : "Unhelpful"}
                        </span>
                      )}
                      {hasRating && (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            color: "var(--saffron-deep)",
                            fontWeight: 600,
                          }}
                        >
                          {Array.from({ length: 5 }).map((_, index) => (
                            <Star
                              key={index}
                              size={13}
                              fill={index < m.rating ? "currentColor" : "none"}
                              stroke="currentColor"
                            />
                          ))}
                          <span style={{ marginLeft: 2 }}>({m.rating}/5)</span>
                        </span>
                      )}
                      {m.flagged && (
                        <button
                          type="button"
                          onClick={() => toggleFlagDetails(m._id)}
                          className="badge badge-saffron"
                          style={{
                            padding: "3px 8px",
                            border: "none",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Flag
                            size={10}
                            style={{ verticalAlign: "middle", marginRight: 2 }}
                          />
                          Flagged
                        </button>
                      )}
                    </div>
                  )}

                  {m.flagged && expandedFlagDetails[m._id] && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: "14px 16px",
                        background: "var(--saffron-tint)",
                        borderRadius: 12,
                        border: "1px solid rgba(194, 108, 27, 0.16)",
                      }}
                    >
                      {m.flag_reason && (
                        <div
                          style={{
                            fontSize: 13,
                            color: "var(--ink-700)",
                            marginBottom: 8,
                            fontFamily: "var(--serif)",
                          }}
                        >
                          <strong>Flag reason:</strong> {m.flag_reason}
                        </div>
                      )}
                      {m.corrected_answer && (
                        <div style={{ marginBottom: 8 }}>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              color: "var(--ink-500)",
                              marginBottom: 4,
                            }}
                          >
                            Correct answer
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              color: "var(--ink-800)",
                              whiteSpace: "pre-wrap",
                              lineHeight: 1.6,
                            }}
                          >
                            {m.corrected_answer}
                          </div>
                        </div>
                      )}
                      {feedbackReferences && (
                        <div>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              color: "var(--ink-500)",
                              marginBottom: 4,
                            }}
                          >
                            References
                          </div>
                          {Array.isArray(feedbackReferences) ? (
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                              {feedbackReferences.map((reference, idx) => (
                                <li
                                  key={idx}
                                  style={{
                                    fontSize: 13,
                                    color: "var(--ink-800)",
                                    marginBottom: 4,
                                  }}
                                >
                                  {reference}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div
                              style={{
                                fontSize: 13,
                                color: "var(--ink-800)",
                                whiteSpace: "pre-wrap",
                                lineHeight: 1.6,
                              }}
                            >
                              {feedbackReferences}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
